import { computed, inject, Injectable } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { EVENT } from '@app/analytics/analytics.cnst'
import { AnalyticsService } from '@app/analytics/analytics.service'
import { decorate, ErrorHandlerType } from '@app/decorators/decorators'
import { tryCatch } from '@app/decorators/tryCatch.decorator'
import { di } from '@app/srv/di.service'
import { ErrorService } from '@app/srv/error.service'
import { dispatch, getState, select2 } from '@app/srv/store.service'
import {
  _anyToError,
  _errorDataAppend,
  _findLast,
  _isTruthy,
  _Retry,
  _sortBy,
  _stringMapValues,
  _uniq,
} from '@naturalcycles/js-lib'
import {
  PaymentMethodInput,
  PaymentMethodType,
  Price,
  ProductKey,
  ProductType,
} from '@naturalcycles/shared'
import { NCInAppPurchase } from '@src/typings/capacitor.js'
import {
  IAPProduct,
  IAPProductRaw,
  IAPTransaction,
  IAPTransactionRaw,
} from '@src/typings/iap.model.js'
import { isIOSApp } from '../cnst/userDevice.cnst.js'
import {
  IAP_PRODUCT_BY_NAME,
  IAP_PRODUCT_KEYS_DEFAULT,
  IAP_PRODUCT_KEYS_WITH_IAP_FEE,
} from '../util/product.util.js'
import { sentryService } from './sentry.service.js'

@Injectable({ providedIn: 'root' })
export class IAPService {
  private analyticsService = inject(AnalyticsService)
  private productState = toSignal(select2(s => s?.product))
  private cartState = toSignal(select2(s => s?.cart))
  private iapProducts = toSignal(select2(s => s?.ui.iapProducts))
  private paymentMethods = toSignal(select2(s => s?.payment.methods))
  private orderCart = toSignal(select2(s => s?.order.cart))
  private appleTaxEnabled = toSignal(select2(s => !!s?.remoteConfig.appleTaxEnabled))

  private currencyMismatch = computed(() => {
    const productState = this.productState()
    const iapProducts = this.iapProducts()
    if (!productState) return false
    const subscription = productState.items.find(p => p.type === ProductType.SUBSCRIPTION)
    if (!subscription || !iapProducts?.[0]) return false

    const iapCurrency = iapProducts[0].currency
    const userCurrency = getState().userLocale.pricingCurrency

    if (iapCurrency === userCurrency) return false

    this.analyticsService.trackEvent(EVENT.IAP_CURRENCY_MISMATCH, { iapCurrency, userCurrency })
    return true
  })

  private iapProductKeyMap = computed(() => {
    const appleTaxEnabled = this.appleTaxEnabled()
    const currencyMismatch = this.currencyMismatch()

    if (!appleTaxEnabled || currencyMismatch) return IAP_PRODUCT_KEYS_DEFAULT
    return IAP_PRODUCT_KEYS_WITH_IAP_FEE
  })

  public iapFee = computed(() => {
    const cartState = this.cartState()
    const orderCart = this.orderCart()
    const appleTaxEnabled = this.appleTaxEnabled()
    const currencyMismatch = this.currencyMismatch()
    const paymentMethods = this.paymentMethods()
    const cartItems = orderCart?.items || cartState?.items

    const subscription = cartItems?.find(item => item.type === ProductType.SUBSCRIPTION)
    const iapAvailable = paymentMethods?.some(m => m.type === PaymentMethodType.IAPAPPLE)

    if (!isIOSApp || !iapAvailable || !appleTaxEnabled || currencyMismatch || !subscription) {
      return
    }

    const iapProduct = this.getIapProductByProductKey(subscription.key)
    if (!iapProduct) {
      return
    }

    if (iapProduct.price.amountWithoutDiscount <= subscription.price.amountWithoutDiscount) {
      return
    }

    return new Price(iapProduct.price).add(new Price(subscription.price).multiply(-1))
  })

  public async subscribe(productId: string): Promise<IAPTransaction | void> {
    const rawTransaction = await NCInAppPurchase.subscribe({ productId })
    return this.enrichTransaction(rawTransaction)
  }

  @tryCatch({
    alert: false,
  })
  public async init(): Promise<void> {
    if (!isIOSApp) return
    const products = await this.getProducts(
      _uniq([
        ..._stringMapValues(IAP_PRODUCT_KEYS_DEFAULT),
        ..._stringMapValues(IAP_PRODUCT_KEYS_WITH_IAP_FEE),
      ]),
    )

    dispatch('extendUI', { iapProducts: products.map(this.enrichProduct) })
  }

  @tryCatch({
    alert: false,
    onError: error => {
      void di.get(ErrorService).showErrorDialog(error)
    },
  })
  public async purchase(): Promise<string | undefined> {
    const subscription = getState().cart.items.find(item => item.type === ProductType.SUBSCRIPTION)
    const productId = this.getIapProductIdByProductKey(subscription!.key)
    if (!productId) return
    try {
      const transaction = await this.subscribe(productId)
      return transaction?.jwsRepresentation
    } catch (err) {
      if (err instanceof Error && err.message.includes('User cancelled')) return
      sentryService.captureException(_anyToError(err), { fingerprint: ['IAPService.purchase'] })
      throw err
    }
  }

  @_Retry({
    maxAttempts: 2,
    delay: 500,
  })
  @decorate({
    errorHandlerType: ErrorHandlerType.LOG,
  })
  public async getExistingIAPSubscription(): Promise<IAPTransaction | undefined> {
    const transactions = await this.getTransactions()
    const enrichedTransactions = transactions.map(this.enrichTransaction).filter(_isTruthy)

    void this.analyticsService.trackEvent(EVENT.RESTORE_IAP, {
      enrichedTransactions,
    })

    const filteredTransactions = enrichedTransactions.filter(
      transaction => transaction?.expirationDate && transaction.expirationDate > Date.now(),
    )

    const sortedPurchases = _sortBy(filteredTransactions, t => t.purchaseDate) // ascending order
    return _findLast(sortedPurchases, p => !!IAP_PRODUCT_BY_NAME[p.productId])
  }

  public async getIAPReceipt(transaction: IAPTransaction): Promise<PaymentMethodInput> {
    return {
      type: PaymentMethodType.IAPAPPLE,
      jwsTransaction: transaction.jwsRepresentation,
      verified: true,
    }
  }

  public getIapProductByProductKey(productKey: ProductKey): IAPProduct | undefined {
    const productId = this.getIapProductIdByProductKey(productKey)
    const { iapProducts } = getState().ui
    return iapProducts.find(p => p.id === productId)
  }

  private getIapProductIdByProductKey(productKey: ProductKey): string {
    return this.iapProductKeyMap()[productKey]!
  }

  private async getTransactions(): Promise<IAPTransactionRaw[]> {
    const { transactions } = await NCInAppPurchase.getTransactions()
    return transactions
  }

  private async getProducts(productIds: string[]): Promise<IAPProductRaw[]> {
    try {
      const { products } = await NCInAppPurchase.getProducts({ productIds })
      return products
    } catch (err) {
      sentryService.captureException(
        _errorDataAppend(err, { fingerprint: ['IAPService.getProducts'] }),
      )
      return []
    }
  }

  private enrichTransaction(transaction?: IAPTransactionRaw): IAPTransaction | undefined {
    if (!transaction) return
    const productKey = IAP_PRODUCT_BY_NAME[transaction.productId]
    if (!productKey) return
    const { userLocale } = getState()
    const currency = transaction.currency || userLocale.pricingCurrency
    const price = new Price(`${transaction.price}[${currency}]`)
    return { ...transaction, productKey, price }
  }

  private enrichProduct(product: IAPProductRaw): IAPProduct {
    const { userLocale } = getState()
    const currency = product.currency || userLocale.pricingCurrency
    const price = new Price(`${product.price}[${currency}]`)
    return { ...product, price }
  }
}
